// src/engine/dataset/DatasetEngine.js
export class DatasetEngine {
  static async create({ worker, config } = {}) {
    const eng = new DatasetEngine({ worker });
    await eng.init(config);
    return eng;
  }

  constructor({ worker, onEvent } = {}) {
    if (!worker) throw new Error("DatasetEngine requires a worker");
    this._worker = worker;
    this._reqId = 0;
    this._pending = new Map();
    this._inited = false;
    this._onEvent = typeof onEvent === "function" ? onEvent : null;
    this._defaultTimeoutMs = 30000; // 30s safety timeout
  this._subs = new Map();
    // NEW: keep last query per dataset for silent requery
    this._lastQueryByDataset = new Map(); // datasetId -> { sql, params }
    // NEW: per-dataset subscribers for live updates (no loading)
    this._subsByDataset = new Map();      // datasetId -> Set<fn({rows,rowCount,at})>
    // NEW: guard to prevent overlapping silent requeries
    this._silentInflight = new Set();     // Set<datasetId>

    this._onMessage = (evt) => {
      const msg = evt?.data;
      if (!msg) return;

      // Support multiple possible shapes from the worker
      const requestId = msg.requestId ?? msg.id;
      const type = msg.type;
      const ok = (typeof msg.ok === "boolean") ? msg.ok : (typeof msg.success === "boolean" ? msg.success : undefined);
      const data = msg.data ?? msg.result ?? msg.rows ?? null;
      const error = msg.error ?? null;

      // Bubble and handle EVENTS
      if (type === "EVENT") {
        // External listener first, if provided
        if (this._onEvent) {
          try { this._onEvent(msg); } catch {}
        }

        // NEW: auto-silent-requery on dataset updates
        const evName = msg?.event?.name;
        if (evName === "dataset:updated") {
          const datasetId = msg?.event?.datasetId;
          if (datasetId) this._silentRequery(datasetId);
        }
        return;
      }

      const resolver = requestId ? this._pending.get(requestId) : null;
      if (!resolver) return;

      // clear pending + timer
      this._pending.delete(requestId);
      if (resolver._t) clearTimeout(resolver._t);

      if (ok === true || (ok === undefined && error == null)) {
        resolver.resolve(data);
      } else {
        const err = new Error(error?.message || "Engine error");
        if (error?.info) err.info = error.info;
        resolver.reject(err);
      }
    };

    this._worker.addEventListener("message", this._onMessage);
  }

  // ————————————————————————————————————————————————————————————
  // Pub/Sub API (dataset-scoped)
  // ————————————————————————————————————————————————————————————
  subscribeDataset(datasetId, fn) {
    if (!datasetId || typeof fn !== "function") return () => {};
    let set = this._subsByDataset.get(datasetId);
    if (!set) { set = new Set(); this._subsByDataset.set(datasetId, set); }
    set.add(fn);
    return () => {
      set.delete(fn);
      if (set.size === 0) this._subsByDataset.delete(datasetId);
    };
  }

  _notifyDataset(datasetId, payload) {
    const set = this._subsByDataset.get(datasetId);
    if (!set || set.size === 0) return;
    for (const fn of set) {
      try { fn(payload); } catch {}
    }
  }

  // ————————————————————————————————————————————————————————————
  // Core call util
  // ————————————————————————————————————————————————————————————
  _call(type, payload, { timeoutMs } = {}) {
    const requestId = `req_${++this._reqId}`;

    return new Promise((resolve, reject) => {
      const entry = { resolve, reject, _t: null };
      this._pending.set(requestId, entry);

      // Timeout so calls don't hang forever if the worker never replies
      const ms = (typeof timeoutMs === "number" && timeoutMs > 0) ? timeoutMs : this._defaultTimeoutMs;
      entry._t = setTimeout(() => {
        // ensure we reject only once
        if (this._pending.has(requestId)) {
          this._pending.delete(requestId);
          reject(new Error(`${type} timed out after ${ms}ms`));
        }
      }, ms);

      this._worker.postMessage({ requestId, type, payload });
    });
  }

  async init(config) {
    if (this._inited) return;
    await this._call("INIT", { config: config || {} }); // ensures db+conn exist in worker
    this._inited = true;
  }

  // Do NOT create tables here; the worker owns table lifecycle.
  async addDataset(dataset, opts = {}) {
    if (!dataset || !dataset._id) throw new Error("dataset._id required");
    const { refreshOnAdd = false, refreshSpec = null } = opts;
    await this.init(); // safety: ensure INIT happened
    return await this._call("ADD_DATASET", { dataset, refreshOnAdd, refreshSpec });
  }

  // Original method
  async refresh(datasetId, refreshSpec) {
    if (!datasetId) throw new Error("datasetId required");
    await this.init();
    return await this._call("REFRESH", { datasetId, refreshSpec });
  }

  // Alias expected by your provider
  async refreshDataset(datasetId, refreshSpec) {
    return this.refresh(datasetId, refreshSpec);
  }

  async setRefreshPlan(datasetId, intervalMs) {
    if (!datasetId) throw new Error("datasetId required");
    await this.init();
    return await this._call("SET_REFRESH_PLAN", { datasetId, intervalMs });
  }

  async listDatasets() {
    await this.init();
    return await this._call("LIST_DATASETS", {});
  }

  // Helper to normalize worker query responses to rows[]
  _extractRows(res) {
    if (Array.isArray(res)) return res;
    if (Array.isArray(res?.rows)) return res.rows;
    if (Array.isArray(res?.data)) return res.data;
    return [];
  }

  // Supports:
  //   query(datasetId, sql, params)
  //   query({ datasetId, sql, params })
  async query(datasetIdOrOpts, sqlMaybe, paramsMaybe) {
    await this.init();
    let datasetId = datasetIdOrOpts;
    let sql = sqlMaybe;
    let params = paramsMaybe;

    if (datasetIdOrOpts && typeof datasetIdOrOpts === "object") {
      sql = datasetIdOrOpts.sql ?? "";
      params = datasetIdOrOpts.params ?? params;
      if (datasetIdOrOpts.datasetId) datasetId = datasetIdOrOpts.datasetId;
    }

    if (typeof sql === "string") sql = sql.trim();
    if (!sql) throw new Error("sql is required");

    // Track last query for silent updates
    if (datasetId) this._lastQueryByDataset.set(datasetId, { sql, params });

    const res = await this._call("QUERY", { datasetId, sql, params });
    return this._extractRows(res);
  }

  // ========= NEW: automatic silent requery on dataset:updated =========
  async _silentRequery(datasetId) {
    if (!datasetId) return;
    // No previous query → nothing to refresh for this consumer
    const last = this._lastQueryByDataset.get(datasetId);
    if (!last) return;

    // Avoid overlapping re-queries per dataset
    if (this._silentInflight.has(datasetId)) return;
    this._silentInflight.add(datasetId);

    try {
      const res = await this._call("QUERY", {
        datasetId,
        sql: last.sql,
        params: last.params
      });
      const rows = this._extractRows(res);
      const rowCount = Array.isArray(res?.rows) ? res.rows.length
                    : Array.isArray(rows) ? rows.length
                    : (res?.rowCount ?? rows.length);

      // Fan out to subscribers (no loading flags here)
      this._notifyDataset(datasetId, { rows, rowCount, datasetId, at: Date.now() });
    } catch (e) {
      // Silent by design; background refresh shouldn't error the UI
      // Optionally log:
      // console.warn("[DatasetEngine] silent requery failed:", e?.message || e);
    } finally {
      this._silentInflight.delete(datasetId);
    }
  }

  // ========= Reinitialize the DuckDB backend =========
  async reinitDb(options = {}) {
    const { timeoutMs, ...payload } = options || {};
    return await this._call("REINIT_DB", payload, { timeoutMs });
  }

  // ========= Info & flush =========
  async getEngineInfo() {
    return await this._call("GET_ENGINE_INFO", {});
  }

  async flushFiles() {
    return await this._call("FLUSH_FILES", {});
  }

  async stopAll() {
    try { return await this._call("STOP_ALL", {}); }
    finally {
      try { this._worker.removeEventListener("message", this._onMessage); } catch {}
    }
  }

  // Optional explicit cleanup
  destroy() {
    try { this._worker.removeEventListener("message", this._onMessage); } catch {}
    this._pending.forEach((resolver) => {
      try { if (resolver._t) clearTimeout(resolver._t); } catch {}
      try { resolver.reject(new Error("Engine destroyed")); } catch {}
    });
    this._pending.clear();
    this._inited = false;
    this._subsByDataset.clear();
    this._lastQueryByDataset.clear();
    this._silentInflight.clear();
  }
}
